<script>
  import { onMount } from 'svelte';
  import { ComponentType } from '../../../../../../js/constants';
  import { getCurrentUrl } from '../../../../../../js/stores';
  import { onTouchOrClick } from '../../../../../../js/onTouchOrClick';
  import { blurOnClick } from '../../../../../../js/blurOnClick';
  import { transitionHeight } from '../../../../../../js/transitionHeight';
  import { urlsAreEqual } from '../../../../../../js/urlUtils';

  import Link from './Link/Link.svelte';

  const TRANSITION_DURATION = '300ms';
  const TRANSITION_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

  const currentUrlStore = getCurrentUrl();

  export let title;
  export let navItems = [];

  let isOpen = false;
  let isMounted = false;
  const currentPage = navItems.find((item) => urlsAreEqual(item.url, $currentUrlStore));
  const currentPageTitle = currentPage && currentPage.title;

  // Toggle the flyout
  function toggle() {
    isOpen = !isOpen;
  }

  onMount(() => {
    isMounted = true;
  });
</script>

<div class="HeaderMenu">
  {#if navItems.length}
    <button
      class="toggle nav-button"
      class:isOpen
      aria-haspopup="true"
      aria-expanded={isOpen ? true : false}
      use:blurOnClick
      use:onTouchOrClick={toggle}
    >
      <span class="truncate-wrapper">
        <span class="title">{title}</span>
        {#if currentPageTitle && !isOpen}
          <span class="current-page">{currentPageTitle}</span>
        {/if}
      </span>
    </button>
    <ul
      class="nav-item-list"
      class:isOpen
      class:hidden={!isMounted}
      data-component-type={ComponentType.SUBNAVIGATION_MENU}
      use:transitionHeight={{
        isOpen,
        duration: TRANSITION_DURATION,
        easing: TRANSITION_EASING,
      }}
    >
      {#each navItems as item}
        <li class="nav-item" class:hidden={item.hidden}>
          <Link id={item.id} title={item.title} url={item.url} isSelected={urlsAreEqual(item.url, $currentUrlStore)} />
        </li>
      {/each}
    </ul>
  {:else}
    <div class="truncate-wrapper"><span class="title">{title}</span></div>
  {/if}
</div>

<style>@charset "UTF-8";
.HeaderMenu {
  width: 100%;
}

.toggle.nav-button {
  cursor: pointer;
  appearance: none;
  box-sizing: border-box;
  overflow: hidden;
  display: block;
  position: relative;
  width: 100%;
  margin: 0;
  padding: 0;
  outline: 0;
  border: 0;
  color: var(--color-text, #1a1a1a);
  font-family: "Square Sans Text VF", "Square Sans Text", "Helvetica", "Arial", "Hiragino Kaku Gothic Pro", "ヒラギノ角ゴ Pro W3", "メイリオ", meiryo, "ＭＳ Ｐゴシック", sans-serif;
  font-size: 16px;
  line-height: 28px;
  font-weight: 400;
  letter-spacing: normal;
  text-align: left;
  text-decoration: none;
  user-select: none;
  -webkit-user-drag: none;
  -webkit-tap-highlight-color: transparent;
  position: relative;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
  background-color: transparent;
  padding-right: 25px;
  transition: none;
}
.toggle.nav-button::after {
  content: "";
  display: block;
  position: absolute;
  width: 8px;
  height: 8px;
  right: 35px;
  border-style: solid;
  border-width: 0;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-color: var(--color-text, #1a1a1a);
  transform: translate3d(50%, calc(-50% - 2px), 0) rotate(45deg);
  transition: transform 300ms cubic-bezier(0.4, 0, 0.2, 1);
  top: 34px;
}
.toggle.nav-button.isOpen::after {
  transform: translate3d(50%, calc(-50% + 2px), 0) rotate(-135deg);
}
.toggle.nav-button::-moz-focus-inner {
  border: 0;
}
.toggle.nav-button:focus {
  outline: none;
}
.toggle.nav-button::before {
  content: "";
  pointer-events: none;
  display: block;
  position: absolute;
  inset: 0;
  background-color: var(--color-highlight, rgba(26, 26, 26, 0.1));
  border-radius: 0;
  opacity: 0;
  transition: opacity 100ms cubic-bezier(0.4, 0, 0.2, 1), transform 100ms cubic-bezier(0.4, 0, 0.2, 1);
  transform: scale(0);
}
.toggle.nav-button:focus:not(:active)::before {
  opacity: 1;
  transform: scale(1);
}
.toggle.nav-button > * {
  position: relative;
}

.truncate-wrapper {
  display: block;
  padding: 20px 25px;
  color: var(--color-text, #1a1a1a);
  font-family: "Square Sans Text VF", "Square Sans Text", "Helvetica", "Arial", "Hiragino Kaku Gothic Pro", "ヒラギノ角ゴ Pro W3", "メイリオ", meiryo, "ＭＳ Ｐゴシック", sans-serif;
  font-size: 16px;
  line-height: 28px;
  font-weight: 400;
  letter-spacing: normal;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.title {
  font-weight: var(--font-weight-medium, 500);
}

.current-page::before {
  content: " → ";
  display: inline;
  margin: 0 4px;
  font-weight: var(--font-weight-medium, 500);
}

.nav-item-list {
  display: block;
  box-sizing: border-box;
  padding: 0;
  margin: 0;
  list-style-type: none;
  width: 100%;
  overflow: hidden;
  position: absolute;
  border-bottom-width: 1px;
  border-bottom-style: solid;
  border-color: var(--color-border, #d9d9d9);
  background-color: var(--color-background, #fff);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.16);
  clip-path: inset(0 0 -10px 0);
}
.nav-item-list > li {
  display: block;
  position: static;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}
.nav-item-list > li::before {
  display: none;
}

.nav-item:last-child {
  padding-bottom: 15px;
}</style>
